import { castDate } from "@caredna-marlin/utils/dates";
import { MbscCalendarEventData } from "@mobiscroll/react";
import { addMinutes, addSeconds, differenceInMinutes } from "date-fns";
import { CommandIndexCombinedDataQuery, VisitEventStatusEnum } from "~/graphql/__generated__/graphql";
import { ServiceLine, VisitType } from "~/providers/OrgConfigProvider";
import { defaultServiceLineSchedulerConfig } from "@caredna-marlin/utils/configurationTypes";
import { defaultVisitTypeSchedulerConfig } from "@caredna-marlin/utils/configurationTypes";
import { LocalVisit, LocalVisitResource } from "~/hooks/visitDragDropReducer";
import { visitResourceStatus } from "./statuses";
import { UNASSIGNED_PROVIDER_ID } from "./visitsHelper";
import { OrgConfig } from "~/providers/OrgConfigProvider";

// Demo: for lab drop-off visits, render a synthetic 5–15 min drive time
// deterministically derived from the patient ID. When the stored drive time
// is zero we also shift the visit's rendered start/end forward by that amount
// so the buffer doesn't overlap the prior event.
const isLabDropOffVisitType = (name: string | undefined | null): boolean => {
  if (!name) return false;
  const lower = name.toLowerCase();
  return lower.includes("lab") && lower.includes("drop");
};

const deterministicLabDropOffDriveMinutes = (patientId: number | null | undefined): number => {
  if (!patientId) return 10;
  // 5–15 inclusive (11 distinct values)
  return 5 + (Math.abs(patientId) % 11);
};

/**
 * For lab drop-off visits with no stored drive time, returns adjusted
 * start/end times (shifted forward by a deterministic 5–15 min) and the
 * synthesized bufferBefore. For everything else returns the unmodified values.
 */
export const adjustVisitTimesForLabDropOff = (
  visitResource: LocalVisitResource,
  orgConfig?: OrgConfig,
): { startTime: string; endTime: string; bufferBefore?: string } => {
  let startTime = visitResource.startTime ? String(visitResource.startTime) : '';
  let endTime = visitResource.endTime ? String(visitResource.endTime) : '';

  const storedDrive = visitResource.startOfShiftDriveTime || visitResource.interVisitDriveTime;
  let bufferBefore: string | undefined = storedDrive > 0 ? storedDrive.toString() : undefined;

  const visit = visitResource.visit;
  const visitTypeName = orgConfig?.visitTypes?.[visit.visitTypeId]?.name;
  if (!bufferBefore && isLabDropOffVisitType(visitTypeName)) {
    const synthesizedDrive = deterministicLabDropOffDriveMinutes(visit.patient?.id ?? null);
    bufferBefore = synthesizedDrive.toString();
    if (startTime && endTime) {
      startTime = addMinutes(castDate(startTime), synthesizedDrive).toISOString();
      endTime = addMinutes(castDate(endTime), synthesizedDrive).toISOString();
    }
  }

  return { startTime, endTime, bufferBefore };
};
/*
  For placing common utility functions for Command Center calendar events.
*/

export type CalendarErrorType = 'drive_time' | 'save';

export interface CalendarError {
  type: CalendarErrorType,
  visit: LocalVisit,
  visitType?: VisitType,
  message: string,
}

export interface CustomMbscCalendarEventData extends MbscCalendarEventData {
  type: "shift" | "visit_resource" | "visit_request" | "calendar_block" | "calendar_block_filter";
  order: number;
  dbDate: string;
  referenceId: number;
  providerId: number | null;
  bufferBefore?: string;
  bufferAfter?: string;
  dragBetweenResources?: boolean;
  dragInTime?: boolean;
}

// Type for visit events only
export type MbscDraggableEventData = CustomMbscCalendarEventData & {
  type: "visit_resource" | "visit_request" | "calendar_block";
  start: string;
  end: string;
};

// Filter function to calendar events that can be dragged.
export type FilterToDraggableOptions = {
  allowNonDraggable?: boolean;
};

export const filterToDraggable = (
  event: CustomMbscCalendarEventData,
  options: FilterToDraggableOptions = {}
): MbscDraggableEventData | undefined => {
  if (!event.start || !event.end) return;
  if (event.type !== "visit_resource" && event.type !== "visit_request" && event.type !== "calendar_block") return;

  let normalizedEvent: CustomMbscCalendarEventData = {
    ...event,
    providerId: event.providerId ?? null,
  };

  if (!normalizedEvent.dragInTime && !normalizedEvent.dragBetweenResources) {
    if (!options.allowNonDraggable) {
      return;
    }

    normalizedEvent = {
      ...normalizedEvent,
      dragInTime: true,
      dragBetweenResources: normalizedEvent.dragBetweenResources ?? false,
    };
  }

  return normalizedEvent as MbscDraggableEventData;
};

export type LocalVisitRequest = {
  id: number,
  taskId: number,
  date: Date,
  visitType: VisitType,
  serviceLine?: ServiceLine,
  duration: number,
  serviceIds: string[],
  patient: CommandIndexCombinedDataQuery["task"][number]["patient"],
}

export type DraggedVisitResource = CommandIndexCombinedDataQuery["visitResource"][number] & {
  offset?: number;
  duration?: number;
  visitResourceRequirementId?: number;
};

// Group events by resource (provider or patient depending on the view)
const groupEventsByResource = (events: CustomMbscCalendarEventData[]): { [key: number]: CustomMbscCalendarEventData[] } => {
  return events.reduce((groups, event) => {
    const resourceId = event.resource;
    if (!groups[parseInt(`${resourceId}`)]) {
      groups[parseInt(`${resourceId}`)] = [];
    }
    groups[parseInt(`${resourceId}`)].push(event);
    return groups;
  }, {} as { [key: number]: CustomMbscCalendarEventData[] });
};

/* Adjust overlapping events for each provider for cases where
   end time of earlier event = (start time of next event -drive time) exactly.
   For Reference https://linear.app/medarrive/issue/CDNA-242/command-center-mobiscroll-event-stacking-issues
*/
const adjustOverlappingEventsForProvider = (
  events: CustomMbscCalendarEventData[]
): CustomMbscCalendarEventData[] => {
  // Sort events by start time
  const sortedEvents = events.sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );

  let previousEndTime: Date | null = null;

  return sortedEvents.map((event) => {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    const driveTimeMinutes = event.bufferBefore ? parseInt(event.bufferBefore, 10) || 0 : 0;

    // Calculate the adjusted start time by subtracting drive time
    const adjustedStartDate = addMinutes(startDate, -driveTimeMinutes);

    // If the current event's adjusted start time (accounting for drive time) collides with the previous event's end time
    if (previousEndTime && differenceInMinutes(adjustedStartDate, previousEndTime) === 0) {
      // Adjust the start time by adding a small gap to avoid stacking
      // NOTE: 7 seconds where the smallest amount of time for Mobiscroll to avoid stacking.
      const adjustedStart = addSeconds(startDate, 7);
      event.start = castDate(adjustedStart).toISOString(); // Update start date with adjusted time
    }

    previousEndTime = endDate;

    return event;
  });
};

/* Main function to adjust all events grouped by provider
   Adjust overlapping events for each provider for cases where
   end time of earlier event = (start time of next event -drive time) exactly.
   For Reference https://linear.app/medarrive/issue/CDNA-242/command-center-mobiscroll-event-stacking-issues
*/
export const adjustOverlappingEvents = (events: CustomMbscCalendarEventData[]): CustomMbscCalendarEventData[] => {
  // Group events by resource (provider in provider view, patient in patient view)
  const groupedEvents = groupEventsByResource(events);

  // Adjust overlapping events for each provider group
  const adjustedEvents: CustomMbscCalendarEventData[] = [];

  Object.keys(groupedEvents).forEach((resourceId) => {
    const resourceEvents = groupedEvents[parseInt(resourceId)];
    const adjustedResourceEvents = adjustOverlappingEventsForProvider(resourceEvents);
    adjustedEvents.push(...adjustedResourceEvents);
  });

  return adjustedEvents;
};

/**
 * Applies the time offset from `before` → `after` to a target date.
 *
 * @param before - The earlier reference time (Date or ISO string)
 * @param after - The later reference time (Date or ISO string)
 * @param target - The time to which the offset should be applied (Date or ISO string)
 * @returns ISO string of the adjusted time
 */
export function applyOffsetIso(
  before: Date | string,
  after: Date | string,
  target: Date | string
): string {
  const beforeDate = new Date(before);
  const afterDate = new Date(after);
  const targetDate = new Date(target);

  const offsetMs = afterDate.getTime() - beforeDate.getTime();
  const adjusted = new Date(targetDate.getTime() + offsetMs);

  return adjusted.toISOString();
}

const defaultPreferences = {
  ...defaultServiceLineSchedulerConfig,
  ...defaultVisitTypeSchedulerConfig,
};


export const getPreferences = (serviceLine: ServiceLine | undefined, visitType: VisitType | undefined) => {
  return {
    ...defaultPreferences,
    ...(typeof serviceLine?.schedulingConfig === 'object' ? serviceLine.schedulingConfig : {}),
    ...(typeof visitType?.schedulingConfig === 'object' ? visitType.schedulingConfig : {}),
  }
}

// Y-axis order of events in the calendar
const SHIFT_EVENT_CALENDAR_ORDER = 1;
const CALENDAR_BLOCK_FILTER_EVENT_CALENDAR_ORDER = 2;
const CALENDAR_BLOCK_EVENT_CALENDAR_ORDER = 3;
const VISIT_RESOURCE_EVENT_CALENDAR_ORDER = 4;
const VISIT_REQUEST_EVENT_CALENDAR_ORDER = 4;

export const getEventCalendarOrder = (eventType: CustomMbscCalendarEventData["type"]) => {
  if (eventType === "shift") {
    return SHIFT_EVENT_CALENDAR_ORDER;
  } else if (eventType === "calendar_block") {
    return CALENDAR_BLOCK_EVENT_CALENDAR_ORDER;
  } else if (eventType === "calendar_block_filter") {
    return CALENDAR_BLOCK_FILTER_EVENT_CALENDAR_ORDER;
  } else if (eventType === "visit_resource") {
    return VISIT_RESOURCE_EVENT_CALENDAR_ORDER;
  } else if (eventType === "visit_request") {
    return VISIT_REQUEST_EVENT_CALENDAR_ORDER;
  }
  return 0;
};


const nonDraggableVisitStatuses = new Set([
  VisitEventStatusEnum.Canceled,
  VisitEventStatusEnum.Complete,
  VisitEventStatusEnum.EnRoute,
  VisitEventStatusEnum.Arrived,
  VisitEventStatusEnum.InProgress,
]);
/**
 * Creates a visit event from a DB visit resource
 * @param visitResource The visit resource
 * @param lastAddedVisitId The ID of the last added visit
 * @param hasCreateDragEventsPermission Whether the user has permission to create drag events
 * @param modifiedResourceIds All the modified resources
 * @returns A CustomMbscCalendarEventData object
 */
export const createVisitEventFromResource = (
  visitResource: LocalVisitResource,
  lastAddedVisitId: number | null,
  hasCreateDragEventsPermission: boolean = false,
  modifiedResourceIds: Set<number>,
  isOptimizeReviewMode?: boolean,
  isOptimizeReviewLocked?: boolean,
  orgConfig?: OrgConfig,
): CustomMbscCalendarEventData => {
  const { startTime: validStartTime, endTime: validEndTime, bufferBefore } =
    adjustVisitTimesForLabDropOff(visitResource, orgConfig);
  const bufferAfter = visitResource.endOfShiftDriveTime.toString();
  const visit = visitResource.visit;

  const bufferProps = {
    bufferBefore: bufferBefore ?? "",
    bufferAfter: bufferAfter,
  };
  const isLocked = !!isOptimizeReviewLocked;
  const draggable = hasCreateDragEventsPermission && !nonDraggableVisitStatuses.has(visitResourceStatus(visitResource)) && !isLocked;

  let cssClass = visit.id === lastAddedVisitId ? "blink" : "";
  cssClass += `${modifiedResourceIds.has(visitResource.id) ? " dashed-border" : ""}`;

  if (isOptimizeReviewMode) cssClass += ` optimize-review-mode`;
  if (isLocked) cssClass += ` optimize-review-locked`;

  // Return adjusted event object
  return {
    id: visitResource.id,
    referenceId: visitResource.id,
    start: validStartTime,
    end: validEndTime,
    date: castDate(validStartTime).getTime(),
    startDate: castDate(validStartTime),
    endDate: castDate(validEndTime),
    dbDate: visit.startTime,
    resource: visitResource.providerId ?? UNASSIGNED_PROVIDER_ID,
    providerId: visitResource.providerId ?? null,
    title: [visit.patient?.firstName, visit.patient?.lastName].join(" "),
    ...bufferProps,
    type: "visit_resource",
    order: getEventCalendarOrder('visit_resource'),
    cssClass,
    dragBetweenResources: draggable,
    dragInTime: draggable,
  };
};
